import { reactive } from 'vue';
// The grammar's own constants, referenced not re-spelled: this lint and the parser disagreeing about
// what a legal name/function is means the lint rejects formulas the engine happily runs (or vice versa).
import {
  IFS_FN, SUM_FN, isPathName, pathRoot, parsePathName,
  isRetiredFunction, isRetiredIdentifier, retiredFunctionMessage, retiredIdentifierMessage,
  analyzeIterationText,
} from './formulaGraph.js';

/**
 * Reactive cache for the Tax Calculate pages (S1-P3 — versioned formula engine).
 * The live API (`services/taxCalculateApi.js`) is the source of truth; this store
 * holds the mapped, UI-shaped copies that the views/dialogs render and bridges the
 * API DTO (camelCase) ⇄ the request payload, applying the naming canon.
 *
 * A formula is NEUTRAL: nothing on it declares what it is "for". Only what the admin drags
 * into its Expression differentiates it. The header is exactly ชื่อสูตร (`name`) / รหัสสูตร
 * (`outputVar`) / คำอธิบาย (`notes`).
 *
 * Naming canon (canonical across S1-P1 / S1-P2 / S1-P3):
 *  - Constant keys (`constKey`) → trim + UPPER_SNAKE_CASE.
 *  - `outputVar` and every formula variable name → lower_snake_case (trim + lower);
 *    they are consumed verbatim as evaluator-bag variable names by the engine, and
 *    must match the S1-P2 personal_info field keys (also lower_snake).
 *  - `name` is a free-text display label — never normalized, never referenced by an expression.
 *
 * @typedef {Object} TaxCalcVersionDto
 * @property {number} calcVersionID @property {number} taxConfigVersionID @property {string} versionCode
 * @property {string} description @property {boolean} isActive
 * @property {?string} canvasGraphJson  opaque whole-canvas Blockly workspace serialization ({graphVersion:2, workspace}; nvarchar(max); null for legacy versions)
 *
 * @typedef {Object} TaxRuleFormulaDto
 * @property {number} formulaID @property {string} name @property {string} expression
 * @property {string} outputVar @property {number} evalOrder
 * @property {?string} notes @property {boolean} isActive
 *
 * @typedef {Object} UiFormula A render/edit copy of TaxRuleFormulaDto carrying `_uid`.
 * @typedef {Object} UiBracket A render/edit copy of TaxBracketDto carrying `_uid`.
 * @typedef {Object} UiConstant A render/edit copy of TaxCalcConstantDto carrying `_uid`.
 */

// The sandbox function whitelist (mirrors backend grammar).
// SUM is the EXPLICIT reduction of a `[]` iteration — SUMITEMS and its item_* variables are retired.
export const FORMULA_FUNCTIONS = ['MIN', 'MAX', 'ROUND', 'IF', IFS_FN, SUM_FN];

// Common constant keys (seed catalog — admins may add more).
export const KNOWN_CONSTANT_KEYS = [
  'PERSONAL_ALLOWANCE',
  'SPOUSE_ALLOWANCE',
  'CHILD_ALLOWANCE',
  'AMT_RATE',
  'AMT_THRESHOLD',
  'AMT_MIN_TAX',
  'RETIREMENT_GROUP_CAP',
];

// Personal-info field keys the Test-Calculate panel collects (identical to S1-P2).
export const PERSONAL_INFO_FIELDS = [
  { key: 'sex', label: 'เพศ (sex)', type: 'select', options: ['MALE', 'FEMALE'] },
  { key: 'age', label: 'อายุ (age)', type: 'number' },
  { key: 'marital_status', label: 'สถานภาพสมรส (marital_status)', type: 'select', options: ['SINGLE', 'MARRIED', 'WIDOWED', 'DIVORCED'] },
  { key: 'spouse_info_status', label: 'ข้อมูลคู่สมรส (spouse_info_status)', type: 'select', options: ['NO_INCOME', 'HAS_INCOME', 'UNKNOWN'] },
  { key: 'form_type', label: 'ประเภทแบบ (form_type)', type: 'select', options: ['90', '91', '94'] },
  { key: 'tax_year', label: 'ปีภาษี (tax_year)', type: 'textbox' },
  { key: 'withholding_tax', label: 'ภาษีหัก ณ ที่จ่าย (withholding_tax)', type: 'number' },
];

// §40(5) ค่าเช่าทรัพย์สิน — lump-sum (เหมา) expense options + their standard rates. Drives the preview's
// asset-type picker (TaxCalculateList), which sends the chosen option with the run request.
// Source of truth: public/taxConfigSeedData.json (EXP_40_5.expense_options). The engine resolves the
// chosen option to the per-rule bridge var `<rule_code_lower>_rate` via the version constant
// EXPENSE_RATE_<OPTION_CODE>, so these codes MUST match the constant keys authored on the version.
export const EXPENSE_40_5_OPTIONS = [
  { optionCode: 'BUILDING_30', label: 'บ้าน/อาคาร/สิ่งปลูกสร้าง/แพ', rate: 0.30, isDefault: true },
  { optionCode: 'AGRI_LAND_20', label: 'ที่ดินที่ใช้ในการเกษตร', rate: 0.20, isDefault: false },
  { optionCode: 'NON_AGRI_LAND_15', label: 'ที่ดินที่ไม่ได้ใช้ในการเกษตร', rate: 0.15, isDefault: false },
  { optionCode: 'VEHICLE_30', label: 'ยานพาหนะ', rate: 0.30, isDefault: false },
  { optionCode: 'OTHER_10', label: 'ทรัพย์สินอื่น', rate: 0.10, isDefault: false },
  { optionCode: 'ACTUAL', label: 'หักตามจริง (กรอกจำนวนเอง)', rate: null, isDefault: false },
];

// Base catalog of variable names always in scope (used by client lint before the
// live `GET /variables` catalog has loaded, and as a floor afterwards).
export const BASE_VARIABLES = [
  // personal_info
  'sex', 'age', 'marital_status', 'spouse_info_status', 'form_type', 'tax_year', 'withholding_tax',
  // derived globals
  'gross_income', 'total_income', 'expense_deduction', 'total_deductions', 'total_allowances', 'net_income',
];

let uidSeq = 1;
function nextUid() {
  uidSeq += 1;
  return uidSeq;
}

const state = reactive({
  /** @type {?number} currently selected Tax Config Version (FK) */
  configVersionId: null,
  /** @type {TaxCalcVersionDto[]} calc versions of the selected config version */
  versions: [],
  /** @type {?TaxCalcVersionDto} version currently open in the editor */
  currentVersion: null,
  /** @type {UiFormula[]} working copy of formulas (by evalOrder) */
  formulas: [],
  /** @type {UiBracket[]} working copy of brackets (by sortOrder) */
  brackets: [],
  /** @type {UiConstant[]} working copy of constants */
  constants: [],
  /** @type {?Object} TaxVariableCatalogDto from GET /variables (lazy) */
  variableCatalog: null,
  /** @type {?Object} last stateless /run result */
  lastRun: null,
  /** @type {?Object} last single-formula preview result (TaxPreviewFormulaResultDto) */
  lastPreview: null,
});

function trimUpper(value) {
  return String(value ?? '').trim().toUpperCase();
}
function trimLower(value) {
  return String(value ?? '').trim().toLowerCase();
}
function trimOnly(value) {
  return String(value ?? '').trim();
}
function toNum(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function setConfigVersion(id) {
  state.configVersionId = id != null ? Number(id) : null;
}
function setVersions(dtoArray) {
  state.versions = Array.isArray(dtoArray) ? dtoArray.slice() : [];
}
function setVariableCatalog(catalog) {
  state.variableCatalog = catalog || null;
}
function setLastRun(result) {
  state.lastRun = result || null;
}
function setLastPreview(result) {
  state.lastPreview = result || null;
}

// ── Formula mappers ────────────────────────────────────────────────────
function mapFormulaDtoToUi(dto) {
  return {
    _uid: nextUid(),
    formulaID: dto.formulaID ?? null,
    name: dto.name ?? '',
    expression: dto.expression ?? '',
    expressionGraphJson: dto.expressionGraphJson ?? null,
    outputVar: dto.outputVar ?? '',
    evalOrder: dto.evalOrder ?? 0,
    notes: dto.notes ?? null,
    isActive: dto.isActive !== false,
  };
}
// `name` is sent as-is when blank: the BAL falls back to Name = OutputVar, which is what lets old
// bundles and the seed import through. Do not client-side default it.
function buildFormulaRequest(ui, evalOrder) {
  return {
    name: trimOnly(ui.name),
    expression: trimOnly(ui.expression),
    expressionGraphJson: ui.expressionGraphJson ?? null,
    outputVar: trimLower(ui.outputVar),
    evalOrder: evalOrder != null ? Number(evalOrder) : (toNum(ui.evalOrder)),
    notes: ui.notes ? trimOnly(ui.notes) : null,
    isActive: ui.isActive !== false,
  };
}
function createBlankFormula(seed = {}) {
  return {
    _uid: nextUid(),
    formulaID: null,
    name: seed.name ?? '',
    expression: seed.expression ?? '',
    expressionGraphJson: seed.expressionGraphJson ?? null,
    outputVar: seed.outputVar ?? '',
    evalOrder: seed.evalOrder ?? 0,
    notes: seed.notes ?? null,
    isActive: true,
  };
}

// ── Bracket mappers ────────────────────────────────────────────────────
function mapBracketDtoToUi(dto) {
  return {
    _uid: nextUid(),
    bracketID: dto.bracketID ?? null,
    lowerBound: dto.lowerBound ?? 0,
    upperBound: dto.upperBound ?? null,
    rate: dto.rate ?? 0,
    sortOrder: dto.sortOrder ?? 0,
  };
}
function buildBracketRequest(ui, sortOrder) {
  const upper = ui.upperBound === null || ui.upperBound === undefined || ui.upperBound === ''
    ? null
    : toNum(ui.upperBound);
  return {
    lowerBound: toNum(ui.lowerBound),
    upperBound: upper,
    rate: toNum(ui.rate),
    sortOrder: sortOrder != null ? Number(sortOrder) : toNum(ui.sortOrder),
  };
}
function createBlankBracket(seed = {}) {
  return {
    _uid: nextUid(),
    bracketID: null,
    lowerBound: seed.lowerBound ?? 0,
    upperBound: seed.upperBound ?? null,
    rate: seed.rate ?? 0,
    sortOrder: seed.sortOrder ?? 0,
  };
}

// ── Constant mappers ───────────────────────────────────────────────────
function mapConstantDtoToUi(dto) {
  return {
    _uid: nextUid(),
    constantID: dto.constantID ?? null,
    constKey: dto.constKey ?? '',
    constValue: dto.constValue ?? 0,
    notes: dto.notes ?? null,
  };
}
function buildConstantRequest(ui) {
  return {
    constKey: trimUpper(ui.constKey),
    constValue: toNum(ui.constValue),
    notes: ui.notes ? trimOnly(ui.notes) : null,
  };
}
function createBlankConstant(seed = {}) {
  return {
    _uid: nextUid(),
    constantID: null,
    constKey: seed.constKey ?? '',
    constValue: seed.constValue ?? 0,
    notes: seed.notes ?? null,
  };
}

// ── Whole-version (canvas) plural builders (W3-F) ───────────────────────
// Thin wrappers that map over the canvas node lists and delegate each item to
// the EXISTING singular builder above. Used by the whole-version Save flow to
// shape graphToFormulas()/collectBrackets()/collectConstants() output into the
// bulk request arrays. They do NOT change the singular builders' behaviour.
function buildFormulaRequests(list) {
  return (Array.isArray(list) ? list : []).map((item) => buildFormulaRequest(item));
}
function buildBracketRequests(list) {
  return (Array.isArray(list) ? list : []).map((item) => buildBracketRequest(item));
}
function buildConstantRequests(list) {
  return (Array.isArray(list) ? list : []).map((item) => buildConstantRequest(item));
}

/**
 * Load a `/full` payload into the editor cache (mapped + ordered).
 * @param {Object} fullDto TaxCalcFullDto
 */
function loadFull(fullDto) {
  state.currentVersion = (fullDto && fullDto.version) || null;
  const formulas = (fullDto && Array.isArray(fullDto.formulas)) ? fullDto.formulas : [];
  const brackets = (fullDto && Array.isArray(fullDto.brackets)) ? fullDto.brackets : [];
  const constants = (fullDto && Array.isArray(fullDto.constants)) ? fullDto.constants : [];
  state.formulas = formulas
    .map(mapFormulaDtoToUi)
    .sort((a, b) => (a.evalOrder - b.evalOrder) || ((a.formulaID || 0) - (b.formulaID || 0)));
  state.brackets = brackets
    .map(mapBracketDtoToUi)
    .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.lowerBound - b.lowerBound));
  state.constants = constants
    .map(mapConstantDtoToUi)
    .sort((a, b) => String(a.constKey).localeCompare(String(b.constKey)));
}

function clearEditor() {
  state.currentVersion = null;
  state.formulas = [];
  state.brackets = [];
  state.constants = [];
  state.lastRun = null;
}

/**
 * Flatten the variable catalog DTO into an array of names (lazy / safe).
 * @returns {string[]}
 */
function catalogNames() {
  const c = state.variableCatalog;
  if (!c) return [];
  const groups = [c.personalInfo, c.globals];
  const names = [];
  groups.forEach((g) => {
    if (Array.isArray(g)) g.forEach((v) => { if (v && v.name) names.push(String(v.name)); });
  });
  return names;
}

/**
 * Build the set of variable names currently allowed in an expression: base vars
 * ∪ live catalog ∪ this version's constant keys ∪ all formula outputVars. Used by
 * the client-side lint to flag unknown identifiers before save.
 * @returns {Set<string>}
 */
function allowedVariables() {
  const set = new Set();
  BASE_VARIABLES.forEach((n) => set.add(n));
  catalogNames().forEach((n) => set.add(n));
  state.constants.forEach((c) => { if (c.constKey) set.add(trimUpper(c.constKey)); });
  KNOWN_CONSTANT_KEYS.forEach((k) => set.add(k));
  state.formulas.forEach((f) => { if (f.outputVar) set.add(trimLower(f.outputVar)); });
  // item_amount / item_rate / item_expense are deliberately NOT here any more: they are retired, so
  // they resolve nowhere, and the lint reports them by their migration sentence rather than by the
  // generic "unknown variable" (see lintExpression).
  return set;
}

/**
 * Replace the CONTENT of every single-quoted literal with spaces, keeping the quotes and the total
 * length — so downstream index arithmetic still lines up with the original string. Same "the next
 * quote closes it" rule the tokenizer uses, and safe for the same reason: the literal grammar has no
 * backslash and no escape sequence, so a quote can never appear inside one. An unterminated quote
 * masks to end-of-string, which is what makes the rest of the scan stop inventing identifiers from it.
 *
 * Exported because the Formula List does the same identifier scan over the same expressions to derive
 * its preview inputs: without the mask it reads a text operand ('FEMALE') as a variable and offers an
 * input field for it. (Paths need masking too — see maskPaths, which runs after this.)
 * @param {string} s
 * @returns {string}
 */
export function maskStringLiterals(s) {
  let out = '';
  let i = 0;
  while (i < s.length) {
    if (s[i] !== "'" && s[i] !== '"') { out += s[i]; i += 1; continue; }
    const q = s[i];
    const close = s.indexOf(q, i + 1);
    if (close === -1) { out += q + ' '.repeat(s.length - i - 1); break; }
    out += q + ' '.repeat(close - i - 1) + q;
    i = close + 1;
  }
  return out;
}

// A whole PATH, exactly the backend's PathRegex (TaxCalcEngineBAL): an identifier followed by at
// least one `.ident` or `[]`, with no whitespace anywhere. Spelled once, here, because a scan that
// applies it and a scan that does not disagree about how many names an expression contains.
const PATH_TOKEN_G = /[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*|\[\])+/g;

/**
 * Replace every PATH with spaces of the SAME length, so downstream index arithmetic still lines up.
 *
 * A path is ONE name. `INCOME_40_5.subItems[].items[].amount` names four things that are NOT variables,
 * and any identifier scan that has not masked it first reads it as four — which is how a preview panel
 * came to offer input fields called `จำนวนเงิน (items)` and `จำนวนเงิน (amount)` for a formula that has
 * no such inputs, and how a version with a formula named `amount` would gain a dependency on itself.
 *
 * The backend does exactly this, in exactly this order (literals first, then paths — see
 * TaxCalcEngineBAL.ReferenceScan), and for exactly this reason.
 * @param {string} s  an expression whose literals are ALREADY masked (see maskStringLiterals)
 * @returns {string}
 */
export function maskPaths(s) {
  return String(s == null ? '' : s).replace(PATH_TOKEN_G, (m) => ' '.repeat(m.length));
}

/**
 * The scan surface an identifier sweep may look at: text literals masked, then whole paths masked.
 * ONE function, so every caller that scans an expression for variable names sees the same string.
 * @param {string} s
 * @returns {string}
 */
export function referenceScan(s) {
  return maskPaths(maskStringLiterals(String(s == null ? '' : s)));
}

/**
 * Lightweight client-side lint of a formula expression against the function
 * whitelist + an allowed-variable set. Returns an array of human messages (empty
 * = looks OK). The backend remains authoritative; this is a pre-save hint only.
 * @param {string} expression
 * @param {Set<string>} [allowedSet]
 * @returns {string[]}
 */
function lintExpression(expression, allowedSet) {
  const errors = [];
  const expr = String(expression ?? '').trim();
  if (!expr) {
    errors.push('ต้องกรอกสูตร (expression)');
    return errors;
  }
  // Balanced parentheses.
  let depth = 0;
  for (let i = 0; i < expr.length; i += 1) {
    if (expr[i] === '(') depth += 1;
    else if (expr[i] === ')') { depth -= 1; if (depth < 0) break; }
  }
  if (depth !== 0) errors.push('วงเล็บไม่สมดุล ( )');

  // `.` and `[ ]` are the spelling of a nested PATH now (INCOME_40_5.subItems[].items[].amount), and
  // `"…"` is an accepted literal delimiter, so the three blanket bans that used to live here would
  // reject the formulas the grammar was widened FOR. They are replaced by the same distinction the
  // parser makes: a path is a NAME, so a computed index or a method call on one is still refused —
  // checked below, per token, rather than by banning the characters outright.
  //
  // This lint is a PRE-SAVE HINT and the backend stays authoritative; what it must never do is
  // disagree with the parser about what is legal, because then it either blocks a formula the engine
  // runs or waves through one the engine 400s on.

  const allowed = allowedSet || allowedVariables();
  const funcs = new Set(FORMULA_FUNCTIONS.map((f) => f.toLowerCase()));
  // Blank out literal CONTENT before the identifier scan (quotes kept so offsets — and therefore the
  // "next non-space char is '('" function test — do not shift). Without this, `sex == 'FEMALE'`
  // reports FEMALE as an unknown variable.
  const scanned = maskStringLiterals(expr);
  // A PATH is ONE token, so the identifier pattern has to swallow `.seg` and `[]` — otherwise the scan
  // reads `INCOME_40_5.subItems[].amount` as three names and reports `subItems` and `amount` as unknown
  // variables, which is a red error on the one formula this grammar exists for.
  const re = /[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*|\[[^\]]*\])*/g;
  let m;
  while ((m = re.exec(scanned)) !== null) {
    const token = m[0];
    // Is it a function call? (next non-space char is '(')
    let j = m.index + token.length;
    while (j < scanned.length && /\s/.test(scanned[j])) j += 1;
    const isCall = scanned[j] === '(';
    if (isPathName(token)) {
      // A path is a NAME: never a callee, never indexed. `[^\]]*` above is deliberately GREEDY about
      // what it swallows so `items[0]` arrives here as one token and is REPORTED, instead of being cut
      // at `items` and silently passing as a known variable with a stray `[0]` nobody checks.
      if (isCall) { errors.push(`เรียกเมธอดบนเส้นทางข้อมูลไม่ได้: ${token}() — เส้นทางข้อมูลเป็นชื่อ ไม่ใช่ฟังก์ชัน`); continue; }
      if (!parsePathName(token)) {
        errors.push(`เส้นทางข้อมูลเขียนไม่ถูกต้อง: ${token} — ใช้รูปแบบ ชื่อ.ชื่อ[].ชื่อ และ [] ต้องว่างเสมอ (ระบุลำดับรายการไม่ได้)`);
        continue;
      }
      // Only the ROOT can be checked against a catalog — the segments below it live in the tax form's
      // JSON, which this store has no picture of. A wrong segment is provable only at RUN time.
      const root = pathRoot(token);
      if (/^income_/i.test(root)) continue;
      if (allowed.has(root) || allowed.has(root.toLowerCase()) || allowed.has(root.toUpperCase())) continue;
      errors.push(`ตัวแปรไม่รู้จัก: ${root} (ต้นทางของ ${token})`);
      continue;
    }
    if (isCall) {
      // RETIRED before UNKNOWN: `SUMITEMS(…)` is not a typo, it is a construct that used to work, and
      // the author needs the sentence that says what to write instead — not a list of five names.
      if (isRetiredFunction(token)) { errors.push(retiredFunctionMessage(token)); continue; }
      if (!funcs.has(token.toLowerCase())) errors.push(`ฟังก์ชันไม่รู้จัก: ${token}() (รองรับเฉพาะ ${FORMULA_FUNCTIONS.join(', ')})`);
      continue;
    }
    // TRUE / FALSE are keywords of the grammar, not names — IFS's always-matching last branch is
    // written `IFS(…, TRUE, 0)`, and reporting TRUE as an unknown variable would flag every one.
    if (/^(?:TRUE|FALSE)$/i.test(token)) continue;
    // Same reason as the retired function above: item_amount is not an unknown variable, it is a
    // retired one, and the two need different sentences.
    if (isRetiredIdentifier(token)) { errors.push(retiredIdentifierMessage(token)); continue; }
    // Income-input bridge vars (income_40_1, ...) are derived from rule codes.
    if (/^income_/i.test(token)) continue;
    const lower = token.toLowerCase();
    if (allowed.has(token) || allowed.has(lower) || allowed.has(token.toUpperCase())) continue;
    errors.push(`ตัวแปรไม่รู้จัก: ${token}`);
  }
  // The two STRUCTURAL `[]` rules — uncorrelated loops, and a function wrapping the whole iterated
  // computation with no explicit SUM. Both are decided by SHAPE alone, so they need no catalog and no
  // data, and both are things the backend refuses at SAVE: without them here the author reads a green
  // lint, presses save and meets a 400. One implementation (formulaGraph.analyzeIteration) is shared
  // with the block generator and the paste dialog, so the three cannot disagree.
  analyzeIterationText(expr).forEach((m) => errors.push(m));
  // De-duplicate.
  return Array.from(new Set(errors));
}

export const taxCalculateStore = {
  state,
  setConfigVersion,
  setVersions,
  setVariableCatalog,
  setLastRun,
  setLastPreview,
  mapFormulaDtoToUi,
  buildFormulaRequest,
  createBlankFormula,
  mapBracketDtoToUi,
  buildBracketRequest,
  createBlankBracket,
  mapConstantDtoToUi,
  buildConstantRequest,
  createBlankConstant,
  buildFormulaRequests,
  buildBracketRequests,
  buildConstantRequests,
  loadFull,
  clearEditor,
  catalogNames,
  allowedVariables,
  lintExpression,
};

export default taxCalculateStore;
